import type { Category, CategoryPaginator, CategoryQueryOptions } from '@/types';
import { useInfiniteQuery, useQuery } from 'react-query';
import { API_ENDPOINTS } from '@/data/client/endpoints';
import client from '@/data/client';
import { useRouter } from 'next/router';
import { getTypeId } from './utils/utils';
import { mapPaginatorData } from './utils/data-mapper';

function filterCategories(categories: Category[], targetTypeId: number | string | undefined) {
  // Convert typeId to string once to avoid multiple conversions in recursive calls
  if(!targetTypeId) return categories;

  // Recursive function to filter each category
  function filterRecursively(category: Category): Category | undefined {
      // Check if category has a type and if it matches the target type
      // Also automatically keep categories with no type (where type is undefined or null)
      if ((!category.type && !category.type_id) || (targetTypeId && category.type?.id == targetTypeId || category.type_id == targetTypeId)) {
          return {
              ...category,
              // Recursively filter children if they exist
              children: category.children?.map(filterRecursively).filter(c => c) ?? []
          };
      }
      // Return undefined if the category does not match the type and has a type defined
      return undefined;
  }

  // Start the recursive filtering from the top-level categories
  return categories.map(filterRecursively).filter(c => c);
}

export function useCategories(options?: CategoryQueryOptions) {
  const { locale, query } = useRouter();

  const formattedOptions = {
    ...options,
    language: locale,
    type_id: getTypeId(query.types as string),
    limit: options?.limit ?? 1000,
  };

  const {
    data,
    isLoading,
    isFetching,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<CategoryPaginator, Error>(
    [API_ENDPOINTS.CATEGORIES, formattedOptions],
    ({ queryKey, pageParam }) =>
      client.categories.all(Object.assign({}, queryKey[1], pageParam)),
    {
      // keepPreviousData: true,
      staleTime: 1000 * 60 * 5, // Data is fresh for 5 minutes
      cacheTime: 1000 * 60 * 30, // Cache data remains for 30 minutes
      getNextPageParam: ({ current_page, last_page }) =>
        last_page > current_page && { page: current_page + 1 },
    }
  );
  function handleLoadMore() {
    fetchNextPage();
  }
  return {
    // Filter out categories which are not same type as the current type, but keep categories with no type
    categories: filterCategories(data?.pages.flatMap((page) => page.data) ?? [], formattedOptions.type_id) ?? [],
    paginatorInfo: Array.isArray(data?.pages)
      ? data?.pages[data.pages.length - 1]
      : null,
    hasNextPage,
    isLoadingMore: isFetchingNextPage,
    isLoading,
    isFetching,
    error,
    loadMore: handleLoadMore,
  };
}

export const useCategoriesQuery = (options: Partial<CategoryQueryOptions>) => {
  const { data, error, isLoading } = useQuery<CategoryPaginator, Error>(
    [API_ENDPOINTS.CATEGORIES, options],
    ({ queryKey, pageParam }) =>
      client.categories.paginated(Object.assign({}, queryKey[1], pageParam)),
    {
      keepPreviousData: true,
    }
  );

  return {
    categories: data?.data ?? [],
    paginatorInfo: mapPaginatorData(data),
    error,
    loading: isLoading,
  };
};
